import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, groupBy, map, mergeMap, startWith, throttleTime } from 'rxjs/operators';
import { Set } from 'immutable';
import { requestActions } from './apiHelpers';
import { AppState } from '@reducers/rootReducer';
import { ID, ReduxAction } from '../types';
import { apiObservable } from '@support/api';
import Contact from '@models/Contact';
import { ArchiveViewState } from '@reducers/ui/archiveView';
import { alertUser } from './alerts';

const archivedContactActions = requestActions('ARCHIVED_CONTACT', ['fetchAll', 'fetch']);
const archivedContactBatchActions = requestActions('ARCHIVED_CONTACT_BATCH', ['delete', 'restore']);

export const fetchArchive = archivedContactActions['fetchAll'];
export const fetchMoreArchive = () => ({ type: 'FETCH_MORE_ARCHIVED_CONTACTS' });
export const fetchArchivedContact = (id: ID) => archivedContactActions['fetch']({ id });
// export const deleteArchivedContact = (id: ID) => ({
//   type: 'DELETE_ARCHIVED_CONTACT_BATCH',
//   payload: { contactIDs: Set([id]) },
// });
// export const deleteArchivedContactBatch = (contactIDs: Set<ID>) => ({
//   type: 'DELETE_ARCHIVED_CONTACT_BATCH',
//   payload: { contactIDs },
// });
export const restoreArchivedContact = (id: ID) => ({
  type: 'RESTORE_ARCHIVED_CONTACT_BATCH',
  payload: { contactIDs: Set([id]) },
});
export const restoreArchivedContactBatch = (contactIDs: Set<ID>) => ({
  type: 'RESTORE_ARCHIVED_CONTACT_BATCH',
  payload: { contactIDs },
});
export const setSelectedArchivedContact = (contact: Contact) => ({
  type: 'SET_SELECTED_ARCHIVED_CONTACT',
  payload: { contact },
});
export const setSelectedArchivedContactBatch = (contactBatch: Set<ID>) => ({
  type: 'SET_SELECTED_ARCHIVED_CONTACT_BATCH',
  payload: { contactBatch },
});

/* Selectors */
export const selectArchive = (state: AppState) => state.data.archive;
export const selectArchivedContact = (id: ID) => (state: AppState) =>
  state.data.archive.items.get(id);
export const selectHighlightedArchivedContact = (state: AppState) =>
  state.ui.archiveView.selectedContact;
export const selectSelectedArchivedContact = (state: AppState) => {
  if (state.ui.archiveView.selectedContactBatch.size == 1) {
    const contactID = state.ui.archiveView.selectedContactBatch.first();
    return state.data.archive.items.get(contactID, null);
  }
  return state.ui.archiveView.selectedContact;
};
export const selectSelectedArchivedContactBatch = (state: AppState) =>
  state.ui.archiveView.selectedContactBatch;
export const selectArchiveView = (state: AppState) => state.ui.archiveView;
export const selectActiveArchiveView = (state: AppState): ArchiveViewState | null => {
  const archiveInView = state.ui.location?.pathname == '/archive';

  if (!archiveInView) {
    return null;
  }

  return {
    selectedContactBatch: selectSelectedArchivedContactBatch(state),
    selectedContact: selectSelectedArchivedContact(state),
  };
};

// Epic helpers
const fetchArchivePageObservable = (state$: StateObservable<AppState>, page: number) =>
  apiObservable(
    'GET',
    '/contacts/archived/',
    {
      query: {
        limit: state$.value.data.archive.limit,
        page,
      },
    },
    { apiVersion: 'v2' },
  );

const fetchArchivedContactObservable = (id: ID) =>
  apiObservable('GET', `/contacts/archived/${id}/`, {}, { apiVersion: 'v2' });

// const deleteArchivedObservable = (ids: ID[]) =>
//   apiObservable('DELETE', '/contacts/archived/bulk/', { ids }, { apiVersion: 'v2' });

const restoreArchivedObservable = (ids: ID[]) =>
  apiObservable('PATCH', '/contacts/archived/restore/', { ids }, { apiVersion: 'v2' });

const projectToFetchArchivePage = (state$: StateObservable<AppState>, page: number) =>
  fetchArchivePageObservable(state$, page).pipe(
    map((response) => archivedContactActions['fetchAllSucceeded']({ ...response, page })),
    startWith(archivedContactActions['fetchAllRequested']()),
    catchError((err) => of(archivedContactActions['fetchAllFailed'](err))),
  );

/* Epics */
const fetchArchiveEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType('FETCH_ALL_ARCHIVED_CONTACTS'),
    mergeMap(() => projectToFetchArchivePage(state$, 1)),
  );

const fetchMoreArchiveEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_MORE_ARCHIVED_CONTACTS'),
    mergeMap(() => projectToFetchArchivePage(state$, state$.value.data.archive.page + 1)),
  );

const fetchArchivedContactEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_ARCHIVED_CONTACT'),
    groupBy(({ payload }) => payload.id),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        // For a given archived contact, only re-fetch at most once every 1 minute
        // This data will be overwritten if we refetch the first page of the Archive, but this doesn't seem critical
        throttleTime(1000 * 60 * 1),
        mergeMap(({ payload }) =>
          fetchArchivedContactObservable(payload.id).pipe(
            map((response) =>
              archivedContactActions['fetchSucceeded']({ ...response, contactID: payload.id }),
            ),
            startWith(archivedContactActions['fetchRequested'](payload)),
            catchError((err) => of(archivedContactActions['fetchFailed']({ ...err }))),
          ),
        ),
      ),
    ),
  );

// const deleteArchivedContactsEpic = (action$: Observable<ReduxAction>) =>
//   action$.pipe(
//     ofType('DELETE_ARCHIVED_CONTACT_BATCH'),
//     mergeMap(({ payload }) => {
//       const contactIDs = payload.contactIDs.toArray();
//       return deleteArchivedObservable(contactIDs).pipe(
//         map(() => archivedContactBatchActions['deleteSucceeded'](payload)),
//         startWith(archivedContactBatchActions['deleteRequested'](payload)),
//         catchError((err) =>
//           of(archivedContactBatchActions['deleteFailed']({ ...err, ...payload })),
//         ),
//       );
//     }),
//   );

const restoreArchivedContactsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('RESTORE_ARCHIVED_CONTACT_BATCH'),
    mergeMap(({ payload }) => {
      const contactIDs = payload.contactIDs.toArray();
      return restoreArchivedObservable(contactIDs).pipe(
        map(() => archivedContactBatchActions['restoreSucceeded'](payload)),
        startWith(archivedContactBatchActions['restoreRequested'](payload)),
        catchError((err) =>
          of(archivedContactBatchActions['restoreFailed']({ ...err, ...payload })),
        ),
      );
    }),
  );

const archiveActionAlertsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType(
      'RESTORE_ARCHIVED_CONTACT_BATCH_SUCCEEDED',
      // 'DELETE_ARCHIVED_CONTACT_BATCH_SUCCEEDED',
      'RESTORE_ARCHIVED_CONTACT_BATCH_FAILED',
      // 'DELETE_ARCHIVED_CONTACT_BATCH_FAILED',
    ),
    map(({ payload, type }) => {
      // let action = type.includes('RESTORE') ? 'restore' : 'delete';
      let action = 'restore';
      const alertType = type.includes('SUCCEEDED') ? 'success' : 'error';
      const count = payload.contactIDs.size;
      const unit = count > 1 ? 'People' : 'Person';

      if (alertType == 'success') {
        action = `${action}d`;
      } else {
        action = `Failed to ${action}`;
      }

      return alertUser(`${action} ${count} ${unit}`, alertType);
    }),
  );

export const archiveEpic = combineEpics(
  fetchArchiveEpic,
  fetchMoreArchiveEpic,
  fetchArchivedContactEpic,
  // deleteArchivedContactsEpic,
  restoreArchivedContactsEpic,
  archiveActionAlertsEpic,
);
